import { optimoPrivateApi } from '../../axios/axiosInstance'
import { endpoints } from '../../endpoints'

import type {
    NotificationListResponse,
    GetNotificationsParams,
    DismissNotificationResponse,
    DismissAllNotificationsResponse,
} from './notificationActions.types'

/**
 * Fetch from absolute URL (for direct pagination links)
 * Prevents parameter drift when backend adds filters/sorts
 * SECURITY: Validates same-origin to prevent token leakage
 */
/**
 * Fetch from absolute URL with security validation
 * @param url - The absolute URL to fetch from
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise resolving to the response data
 * @throws Error if URL is invalid or cross-origin
 */
export const getAbsolute = <T = NotificationListResponse>(url: string, signal?: AbortSignal): Promise<T> => {
    // Input validation
    if (!url || url.trim().length === 0) {
        throw new Error('Invalid URL: URL must be a non-empty string')
    }

    // Security check: Ensure URL is same-origin to prevent token leakage
    const baseURL = optimoPrivateApi.defaults.baseURL
    if (!baseURL) {
        throw new Error('API base URL not configured')
    }

    try {
        const targetUrl = new URL(url)
        const baseUrl = new URL(baseURL)

        if (targetUrl.origin !== baseUrl.origin) {
            throw new Error('Invalid pagination URL: cross-origin request blocked')
        }
    } catch (error) {
        if (error instanceof Error && error.message.includes('cross-origin')) {
            throw error // Re-throw security errors as-is
        }
        throw new Error(`Invalid pagination URL format: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }

    return optimoPrivateApi.get(url, { baseURL: '', signal }).then((r) => r.data)
}

/**
 * Get notifications with optional parameters
 * @param params - Optional query parameters for filtering/pagination
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise resolving to notification list response
 */
export const getNotifications = async (
    params?: GetNotificationsParams,
    signal?: AbortSignal,
): Promise<NotificationListResponse> => {
    const response = await optimoPrivateApi.get<NotificationListResponse>(endpoints.notifications.list, {
        params,
        signal,
    })
    return response.data
}

/**
 * Get notification count only (optimized for polling)
 * @returns Promise resolving to the notification count
 */
export const getNotificationCount = async (): Promise<number> => {
    const response = await getNotifications({ count_only: true })
    return response.count
}

/**
 * Dismiss a single notification
 * @param notificationId - The ID of the notification to dismiss
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise resolving to dismiss response
 * @throws Error if notificationId is invalid
 */
export const dismissNotification = async (
    notificationId: string,
    signal?: AbortSignal,
): Promise<DismissNotificationResponse> => {
    // Input validation
    if (!notificationId || typeof notificationId !== 'string' || notificationId.trim().length === 0) {
        throw new Error('Invalid notification ID: must be a non-empty string')
    }
    const response = await optimoPrivateApi.post<DismissNotificationResponse>(
        endpoints.notifications.dismiss(notificationId),
        {},
        { signal },
    )

    // Handle 204 No Content response (common for idempotent mutations)
    if (response.status === 204) {
        return { success: true, message: 'Notification dismissed' }
    }

    return response.data
}

/**
 * Dismiss all notifications using the backend bulk endpoint
 * @param signal - Optional AbortSignal for canceling the request
 * @returns Promise resolving to dismiss all response
 */
export const dismissAllNotifications = async (signal?: AbortSignal): Promise<DismissAllNotificationsResponse> => {
    const response = await optimoPrivateApi.post<DismissAllNotificationsResponse>(
        endpoints.notifications.dismissAll,
        {},
        { signal },
    )

    // Handle 204 No Content response (common for idempotent mutations)
    if (response.status === 204) {
        return { success: true, message: 'All notifications dismissed', dismissed_count: 0 }
    }

    return response.data
}
